// The password field of every Portal sign-in screen (login, colour PIN,
// account setup, change password). Two shapes, chosen by `allowSave`:
//
//   • true  — an ordinary <input type="password"> with the caller's name and
//     autocomplete hint, so the device's password manager offers to save it
//     and fills it next time (the 2026-10-19 design).
//   • false — the default (savedLogins.ts): a type="text" field drawn as dots
//     by CSS, with no name, autocomplete="off" and the extension opt-outs.
//     Browsers decide "this is a password" from type="password", so there is
//     nothing to save and nowhere to fill a saved one. Copy and cut are
//     blocked while it is hidden; the eye still reveals it.
//
// The two shapes are different elements (keyed), never one element whose
// type flips: Chrome remembers that an input was ever type="password" and
// keeps treating it as one.
//
// A browser that cannot draw text as dots (none current) gets a password
// field with autocomplete="off" instead: hidden on screen, though it may
// still offer to save.

import type { ClipboardEvent, InputHTMLAttributes } from "react";
import { PASSWORD_MANAGER_OPT_OUT, supportsMaskedText } from "./savedLogins";

type SecretInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
    /** Settings › Login screen: may the browser save / fill this credential? */
    allowSave: boolean;
    /** Show the characters (the eye button). */
    revealed?: boolean;
};

const SecretInput = ({ allowSave, revealed = false, className, name, autoComplete, onCopy, onCut, ...rest }: SecretInputProps) => {
    if (allowSave || !supportsMaskedText()) {
        return (
            <input
                key="password-field"
                {...rest}
                type={revealed ? "text" : "password"}
                className={className}
                name={name}
                autoComplete={allowSave ? autoComplete : "off"}
                onCopy={onCopy}
                onCut={onCut}
            />
        );
    }

    const hidden = !revealed;
    const guard = (handler?: (e: ClipboardEvent<HTMLInputElement>) => void) => (e: ClipboardEvent<HTMLInputElement>) => {
        if (hidden) e.preventDefault();
        handler?.(e);
    };
    return (
        <input
            key="masked-field"
            {...rest}
            {...PASSWORD_MANAGER_OPT_OUT}
            type="text"
            className={[className, hidden ? "secret-input--masked" : ""].filter(Boolean).join(" ")}
            autoComplete="off"
            // Keep the keyboard from learning the password as a word it then suggests to the next person.
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            onCopy={guard(onCopy)}
            onCut={guard(onCut)}
        />
    );
};

export default SecretInput;
